import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Sparkles, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";

const dayNames = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

export default function PricingCalendar() {
  const [roomId, setRoomId] = useState("deluxe");
  const [rooms, setRooms] = useState([]);
  const [ym, setYm] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  });
  const [days, setDays] = useState([]);
  const [selected, setSelected] = useState(null);
  const [newPrice, setNewPrice] = useState("");
  const [reason, setReason] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkWd, setBulkWd] = useState("");
  const [bulkWe, setBulkWe] = useState("");

  useEffect(() => { api.get("/rooms").then(r => setRooms(r.data)); }, []);

  const load = () => {
    api.get("/pricing/calendar", { params: { month: ym, room_id: roomId } }).then(r => setDays(r.data.days));
  };
  useEffect(load, [ym, roomId]);

  const [year, monthIdx] = useMemo(() => {
    const [y, m] = ym.split("-").map(Number);
    return [y, m - 1];
  }, [ym]);

  const monthLabel = useMemo(() => {
    return new Date(year, monthIdx).toLocaleString("en-US", { month: "long", year: "numeric" });
  }, [year, monthIdx]);

  const grid = useMemo(() => {
    if (!days.length) return [];
    const first = new Date(year, monthIdx, 1);
    const startPad = first.getDay();
    const cells = new Array(startPad).fill(null);
    days.forEach(d => cells.push(d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [days, year, monthIdx]);

  const goPrev = () => {
    const d = new Date(year, monthIdx - 1, 1);
    setYm(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
  };
  const goNext = () => {
    const d = new Date(year, monthIdx + 1, 1);
    setYm(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
  };

  const openDay = (day) => {
    if (!day) return;
    setSelected(day);
    setNewPrice(String(day.price));
    setReason("");
  };

  const saveOverride = async () => {
    try {
      await api.post("/pricing/override", { date: selected.date, room_id: roomId, price: parseInt(newPrice), reason });
      toast.success(`Rate set for ${selected.date}`);
      setSelected(null);
      load();
    } catch { toast.error("Failed to save"); }
  };

  const resetDay = async () => {
    try {
      await api.delete("/pricing/override", { params: { date_str: selected.date, room_id: roomId } });
      toast.success("Reset to default");
      setSelected(null);
      load();
    } catch { toast.error("Failed"); }
  };

  const bulkApply = async () => {
    try {
      const payload = { month: ym, room_id: roomId };
      if (bulkWd) payload.weekday_price = parseInt(bulkWd);
      if (bulkWe) payload.weekend_price = parseInt(bulkWe);
      const r = await api.post("/pricing/bulk-override", payload);
      toast.success(`Updated ${r.data.updated} dates`);
      setBulkOpen(false);
      setBulkWd(""); setBulkWe("");
      load();
    } catch { toast.error("Bulk update failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-4xl">Pricing Calendar</h1>
          <p className="text-[#8A7970] mt-1">Click any date to set a custom rate. Festival & weekend rates auto-apply.</p>
        </div>
        <Button onClick={() => setBulkOpen(true)} className="rounded-full bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="bulk-edit-btn">
          <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5}/> Bulk Edit
        </Button>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          {rooms.map(r => (
            <button key={r.id} onClick={() => setRoomId(r.id)} data-testid={`room-tab-${r.id}`}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${roomId === r.id ? "bg-[#B84F3E] text-white border-[#B84F3E]" : "bg-white border-[#E7C2A6] text-[#4A403B] hover:bg-[#F5F2EB]"}`}>
              {r.name}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Button variant="outline" size="icon" onClick={goPrev} data-testid="prev-month-btn"><ChevronLeft className="w-4 h-4" strokeWidth={1.5}/></Button>
          <div className="font-serif text-2xl min-w-[180px] text-center">{monthLabel}</div>
          <Button variant="outline" size="icon" onClick={goNext} data-testid="next-month-btn"><ChevronRight className="w-4 h-4" strokeWidth={1.5}/></Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-4 text-xs text-[#8A7970]">
        <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-[#FDF4EF] border border-[#E7C2A6]"/> Weekend (Fri-Sun)</div>
        <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-[#FBE8DA] border border-[#E7C2A6]"/> Festival</div>
        <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 border-2 border-[#B84F3E]"/> Custom override</div>
      </div>

      {/* Grid */}
      <div className="bg-[#FDFBF7] rounded-xl border border-[#E7C2A6]/40 overflow-hidden">
        <div className="grid grid-cols-7 border-b border-[#E7C2A6]/40 bg-[#F5F2EB]">
          {dayNames.map(d => <div key={d} className="p-3 text-xs uppercase tracking-widest text-[#8A7970] text-center">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((cell, i) => {
            if (!cell) return <div key={i} className="border-b border-r border-[#E7C2A6]/30 min-h-[88px] bg-[#FAF9F6]"/>;
            const classes = ["calendar-cell","border-b","border-r","border-[#E7C2A6]/30","p-2.5","cursor-pointer","flex","flex-col"];
            if (cell.is_weekend) classes.push("weekend");
            if (cell.festival) classes.push("festival");
            if (cell.is_override) classes.push("override");
            return (
              <button key={i} onClick={() => openDay(cell)} className={classes.join(" ")} data-testid={`cal-day-${cell.date}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{parseInt(cell.date.slice(8))}</span>
                  {cell.festival && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#B84F3E] text-white">{cell.festival.length > 8 ? cell.festival.slice(0,8) + "…" : cell.festival}</span>}
                </div>
                <div className="mt-auto font-serif text-lg text-[#B84F3E]">₹{cell.price}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="edit-day-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Set Rate · {selected?.date} ({selected?.day})</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {selected?.festival && <div className="text-sm bg-[#FBE8DA] p-3 rounded-lg">Festival: <b>{selected.festival}</b></div>}
            <div>
              <label className="text-sm text-[#8A7970] mb-1.5 block">Price (₹)</label>
              <Input type="number" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} data-testid="override-price-input"/>
            </div>
            <div>
              <label className="text-sm text-[#8A7970] mb-1.5 block">Reason (optional)</label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g., special weekend" data-testid="override-reason-input"/>
            </div>
          </div>
          <DialogFooter>
            {selected?.is_override && <Button variant="outline" onClick={resetDay} data-testid="reset-day-btn"><RotateCcw className="w-4 h-4 mr-2" strokeWidth={1.5}/>Reset to Default</Button>}
            <Button onClick={saveOverride} className="bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="save-override-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="bulk-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Bulk Edit · {monthLabel}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#8A7970]">Set default weekday & weekend prices for all days in this month.</p>
            <div>
              <label className="text-sm text-[#8A7970] mb-1.5 block">Weekday (Mon–Thu) Price</label>
              <Input type="number" value={bulkWd} onChange={(e) => setBulkWd(e.target.value)} placeholder="e.g., 1199" data-testid="bulk-weekday-input"/>
            </div>
            <div>
              <label className="text-sm text-[#8A7970] mb-1.5 block">Weekend (Fri–Sun) Price</label>
              <Input type="number" value={bulkWe} onChange={(e) => setBulkWe(e.target.value)} placeholder="e.g., 1699" data-testid="bulk-weekend-input"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={bulkApply} className="bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="bulk-apply-btn">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
